/**
 * WorkspaceNameEditor — inline editor for the active team workspace name.
 *
 * Renders a small "pill" with the workspace name + a pencil button. The
 * pencil is only enabled when:
 *   • the active space is a TEAM space (personal spaces auto-mirror the
 *     user's name and the backend rejects PATCH on them), AND
 *   • the user owns it (`my_role === "owner"`).
 *
 * Clicking the pencil opens a confirmation dialog with an input. On
 * success we invalidate the spaces query so the sidebar switcher,
 * header, and any other consumer pick up the new name without a full
 * reload.
 *
 * Designed to live in the Dashboard PageHeader. Returns ``null`` when
 * the user only has a personal space (so single-workspace users don't
 * see a redundant control).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Save } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSpace } from "../../contexts/SpaceContext";
import { tx } from "../../i18n";
import { useLang } from "../../contexts/LangContext";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "../form-ux";

export function WorkspaceNameEditor({ space: spaceProp, compact = false, refetchSpaces }) {
  const { lang } = useLang();
  const ctx = useSpace();
  const space = spaceProp || ctx.activeSpace;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const isPersonal = space?.kind === "personal";
  const isOwner = space?.my_role === "owner";
  const canEdit = !!space && !isPersonal && isOwner;

  const Schema = z.object({
    name: z.string().trim()
      .min(1, tx(lang, "Name must be 1–80 characters", "El nombre debe tener 1–80 caracteres", "O nome deve ter 1–80 caracteres"))
      .max(80, tx(lang, "Name must be 1–80 characters", "El nombre debe tener 1–80 caracteres", "O nome deve ter 1–80 caracteres")),
  });

  const form = useZodForm(Schema, { name: "" }, { formId: "workspace-rename" });

  const rename = useMutation({
    mutationFn: async (newName) => {
      const res = await api.patch(`/spaces/${space.id}`, { name: newName });
      return res.data;
    },
    onSuccess: () => {
      toast.success(tx(lang, "Workspace renamed", "Workspace renombrado", "Workspace renomeado"));
      qc.invalidateQueries({ queryKey: ["spaces"] });
      qc.invalidateQueries({ queryKey: ["dashboard-global"] });
      (refetchSpaces || ctx.refetch)?.();
      setOpen(false);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "";
      const msg = detail === "cannot_rename_personal_space"
        ? tx(lang, "Personal space cannot be renamed.", "El espacio personal no se puede renombrar.", "O espaço pessoal não pode ser renomeado.")
        : detail || tx(lang, "Could not rename", "No se pudo renombrar", "Não foi possível renomear");
      toast.error(msg);
    },
  });

  if (!space) return null;

  const openDialog = () => {
    form.reset({ name: space.name || "" });
    setOpen(true);
  };

  const submit = form.handleSubmit(async (values) => {
    const trimmed = values.name.trim();
    if (trimmed === space.name) {
      setOpen(false);
      return;
    }
    await rename.mutateAsync(trimmed);
  });

  // Compact mode: render JUST the pencil button (caller already shows the name).
  if (compact) {
    if (!canEdit) return null;
    return (
      <>
        {/* design-system: ALLOW icon-only pencil toggle (inline with truncated text, Button variant doesn't fit) */}
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          data-testid={`workspace-name-edit-btn-${space.id}`}
          onClick={openDialog}
          className="text-slate-400 hover:text-emerald-700 transition-colors rounded p-1 focus:outline-none focus:ring-2 focus:ring-emerald-200"
          aria-label={tx(lang, "Rename workspace", "Renombrar workspace", "Renomear workspace")}
          title={tx(lang, "Rename workspace", "Renombrar workspace", "Renomear workspace")}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        {renderDialog()}
      </>
    );
  }

  function renderDialog() {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="workspace-rename-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{tx(lang, "Rename workspace", "Renombrar workspace", "Renomear workspace")}</DialogTitle>
            <DialogDescription>
              {tx(lang,
                "Change how this team workspace is shown across the app.",
                "Cambia cómo se muestra este workspace de equipo en toda la app.",
                "Mude como este workspace de equipe aparece em todo o app.")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2" noValidate>
            <FormField
              label={tx(lang, "New name", "Nuevo nombre", "Novo nome")}
              name="name"
              required
              hint={tx(lang, "1–80 characters.", "1–80 caracteres.", "1–80 caracteres.")}
              error={form.errors.name && (form.touched.name || form.submitCount > 0) ? form.errors.name : undefined}
            >
              <FormField.Input
                data-testid="workspace-name-input"
                maxLength={80}
                autoFocus
                {...form.getFieldProps("name")}
              />
            </FormField>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="workspace-rename-cancel"
              >
                {tx(lang, "Cancel", "Cancelar", "Cancelar")}
              </Button>
              <SubmitButton
                submitting={form.submitting || rename.isPending}
                loadingText={tx(lang, "Saving…", "Guardando…", "Salvando…")}
                testid="workspace-rename-submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Save className="h-4 w-4" />
                {tx(lang, "Save", "Guardar", "Salvar")}
              </SubmitButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <div
        data-testid="workspace-name-chip"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 bg-white/80 backdrop-blur-sm text-xs text-slate-700"
        title={space.name}
      >
        <span className="font-semibold max-w-[160px] truncate" data-testid="workspace-name-text">
          {space.name}
        </span>
        {canEdit && (
          // design-system: ALLOW icon-only pencil toggle inside chip (Button variant doesn't fit)
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            data-testid="workspace-name-edit-btn"
            onClick={openDialog}
            className="text-slate-400 hover:text-slate-700 transition-colors rounded p-0.5 -mr-1 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            aria-label={tx(lang, "Rename workspace", "Renombrar workspace", "Renomear workspace")}
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {renderDialog()}
    </>
  );
}
